
import React, { useState, useEffect, use } from 'react';
import { FiShoppingCart, FiX, FiPlus, FiMinus, FiCheck, FiLock, FiTrash2, FiShoppingBag } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { cartAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import CheckoutModal from './CheckoutModal';

const Cart = () => {
  const { isCartOpen, closeCart } = useCart();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated, login } = useAuth();

  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryBgHover = 'hover:bg-yellow-500';
  const primaryLight = 'bg-yellow-50';
  const primaryBorder = 'border-yellow-400';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-400';
  const secondaryBgHover = 'hover:bg-gray-500';
  const secondaryLight = 'bg-gray-100';
  const secondaryBorder = 'border-gray-400';

  // Récupérer le panier
  useEffect(() => {
    if (isCartOpen) {
      fetchCart();
    }
  }, [isCartOpen, isAuthenticated]);

  if (!isCartOpen) {
    return null;
  }

  const fetchCart = async () => {
    try {
      setLoading(true);
      
      if (isAuthenticated) {
        const response = await cartAPI.getCart();
        
        if (response.success) {
          setCartItems(response.data?.items || response.data || []);
        } else {
          console.error('Erreur API:', response.message);
          setCartItems([]);
        }
      } else {
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        setCartItems(guestCart);
      }
      
    } catch (error) {
      console.error('Erreur chargement panier:', error);
      
      if (!isAuthenticated) {
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        setCartItems(guestCart);
      } else {
        setCartItems([]);
      }
      
    } finally {
      setLoading(false);
    }
  };

  const triggerCartUpdate = () => {
    const event = new CustomEvent('cart-updated', { detail: { timestamp: Date.now() } });
    window.dispatchEvent(event);
  };

  // Supprimer un article du panier
  const removeFromCart = async (productId) => {
    try {
      if (isAuthenticated) {
        const remove = {
          user_id : user.id,
          product_id : productId
        }
        await cartAPI.removeFromCart(remove);
      } else {
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        const updatedCart = guestCart.filter(item => item.product_id !== productId);
        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
      }
      
      setCartItems(prev => prev.filter(item => 
        item.product_id !== productId && item.id !== productId
      ));
      
      triggerCartUpdate();
      
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  // Mettre à jour la quantité
  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    try {
      if (isAuthenticated) {
        const updateCart = {
          user_id: user.id,
          product_id: productId,
        }
        await cartAPI.updateCart(updateCart, newQuantity);
      } else {
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        const updatedCart = guestCart.map(item => 
          item.product_id === productId 
            ? { ...item, quantity: newQuantity } 
            : item
        );
        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
      }
      
      setCartItems(prev => prev.map(item => 
        (item.product_id === productId || item.id === productId) 
          ? { ...item, quantity: newQuantity } 
          : item
      ));
      
      triggerCartUpdate();
      
    } catch (error) {
      console.error('Erreur mise à jour quantité:', error);
    }
  };

  // Vider le panier
  const clearCart = async () => {
    try {
      if (isAuthenticated) {
        await cartAPI.clearCart();
      } else {
        localStorage.removeItem('guest_cart');
      }
      
      setCartItems([]);
      triggerCartUpdate();
      
    } catch (error) {
      console.error('Erreur vidage panier:', error);
    }
  };

  // Calcul du prix total
  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const price = item.price || item.unit_price || 0;
      const quantity = item.quantity || 1;
      return total + (price * quantity);
    }, 0);
  };

  // Calcul du nombre total d'articles
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => {
      return total + (item.quantity || 1);
    }, 0);
  };

  // Gérer le checkout
  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Votre panier est vide');
    } else {
      setShowCheckoutModal(true);
    }
  };

  // Gérer la connexion
  const handleLogin = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const username = formData.get('username');
    const password = formData.get('password');

    try {
      const result = await login(username, password);
      
      if (result.success) {
        setShowLoginModal(false);
        
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        if (guestCart.length > 0) {
          for (const item of guestCart) {
            try {
              await cartAPI.addToCart({
                product_id: item.product_id,
                quantity: item.quantity
              });
            } catch (err) {
              console.error('Erreur transfert article:', err);
            }
          }
          localStorage.removeItem('guest_cart');
        }
        
        fetchCart();
        
        setTimeout(() => {
          closeCart();
          navigate('/checkout');
        }, 1000);
        
      } else {
        alert(result.message || 'Erreur de connexion');
      }
    } catch (error) {
      console.error('Erreur connexion:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  // Fonction pour rendre l'URL de l'image
  const getImageUrl = (image) => {
    if (!image) return '/placeholder.jpg';
    
    if (image.startsWith('http') || image.startsWith('blob:')) {
      return image;
    }
    
    if (image.startsWith('/storage/products/')) {
      return `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}${image}`;
    }

    return `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/storage/products/${image}`;
  };

  // Obtenir l'ID du produit
  const getProductId = (item) => {
    return item.product_id || item.id;
  };

  // Obtenir le stock maximum
  const getMaxQuantity = (item) => {
    return item.max_quantity || item.stock || 999;
  };

  if (!isCartOpen) return null;

  return (
    <>
      {isCartOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={closeCart} 
          />
          
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl">
            <div className="flex flex-col h-full">
              {/* Header avec nouvelle charte */}
              <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <div className={`p-2 ${primaryLight} rounded-lg`}>
                      <FiShoppingCart className={`text-xl ${primaryColor}`} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Mon Panier <span className="text-gray-600">({getTotalItems()})</span>
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAuthenticated ? (
                      <span className="text-xs bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full border border-yellow-200 font-medium">
                        💾 Sauvegardé sur votre compte
                      </span>
                    ) : (
                      <span className="text-xs bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 px-3 py-1.5 rounded-full border border-gray-200 font-medium flex items-center gap-1.5">
                        <FiLock size={12} className="text-gray-500" />
                        Panier temporaire (local)
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeCart}
                  className={`p-2 ${secondaryLight} hover:bg-gray-200 rounded-xl transition-all duration-200`}
                >
                  <FiX className="text-xl text-gray-600 hover:text-gray-900" />
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-5">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-flex flex-col items-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-yellow-400 mb-4"></div>
                      <p className="text-gray-600 font-medium">Chargement du panier...</p>
                    </div>
                  </div>
                ) : cartItems.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex flex-col items-center">
                      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-5">
                        <FiShoppingBag className="text-3xl text-gray-400" />
                      </div>
                      <p className="text-gray-800 font-semibold text-lg mb-2">Votre panier est vide</p>
                      <p className="text-gray-500 mb-6">Ajoutez des produits pour commencer vos achats</p>
                      <button
                        onClick={() => {
                          closeCart();
                          navigate('/');
                        }}
                        className={`px-5 py-3 ${primaryBg} text-white rounded-xl font-semibold ${primaryBgHover} shadow-md transition-all duration-200 flex items-center gap-2`}
                      >
                        <FiShoppingBag />
                        <span>Parcourir les produits</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cartItems.map((item) => {
                      const productId = getProductId(item);
                      const maxQuantity = getMaxQuantity(item);
                      
                      return (
                        <div 
                          key={productId} 
                          className="flex gap-4 p-4 border border-gray-200 rounded-xl bg-white hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="relative">
                            <img
                              src={getImageUrl(item.image)}
                              alt={item.name}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.target.src = '/placeholder.jpg';
                              }}
                            />
                            <div className="absolute -top-2 -right-2">
                              <span className={`text-xs font-bold px-2 py-1 ${primaryBg} text-white rounded-full`}>
                                {item.quantity}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1 line-clamp-2">
                              {item.name}
                            </h3>
                            <p className="text-gray-900 font-bold text-base mb-3">
                              {(item.price || item.unit_price || 0).toLocaleString('fr-FR')} FCFA
                            </p>
                            
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => updateQuantity(productId, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                className={`p-1.5 rounded-lg border ${secondaryBorder} disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors`}
                              >
                                <FiMinus className="text-sm text-gray-600" />
                              </button>
                              
                              <span className="font-bold text-gray-900 min-w-[24px] text-center">
                                {item.quantity}
                              </span>
                              
                              <button
                                onClick={() => updateQuantity(productId, item.quantity + 1)}
                                disabled={item.quantity >= maxQuantity}
                                className={`p-1.5 rounded-lg border ${secondaryBorder} disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors`}
                              >
                                <FiPlus className="text-sm text-gray-600" />
                              </button>
                              
                              {/* <div className="text-xs text-gray-500 ml-auto">
                                Max: {maxQuantity}
                              </div> */}
                            </div>
                            
                            <div className="mt-2 text-xs text-gray-600">
                              Sous-total: <span className="font-semibold">
                                {((item.price || item.unit_price || 0) * (item.quantity || 1)).toLocaleString('fr-FR')} FCFA
                              </span>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => removeFromCart(productId)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 self-start"
                            title="Supprimer"
                          >
                            <FiTrash2 className="text-lg" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer - seulement si panier non vide */}
              {cartItems.length > 0 && (
                <div className="border-t border-gray-200 p-5 space-y-5 bg-gradient-to-r from-gray-50 to-white">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-lg">
                      <span className="text-gray-900 font-bold">Total:</span>
                      <span className={`text-gray-900 font-bold ${primaryColor}`}>
                        {getTotalPrice().toLocaleString('fr-FR')} FCFA
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <button
                      onClick={handleCheckout}
                      className={`w-full flex items-center justify-center gap-3 px-6 py-3.5 ${primaryBg} text-white rounded-xl font-bold ${primaryBgHover} shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                      disabled={cartItems.length === 0}
                    >
                      <FiCheck className="text-lg" />
                      <span className="text-base">
                        Valider la commande
                      </span>
                    </button>

                    <button
                      onClick={clearCart}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                    >
                      <FiTrash2 />
                      <span>Vider le panier</span>
                    </button>
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-500 text-center">
                      <p className="mt-1">Livraison rapide dans tout le pays</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal checkout */}
      <CheckoutModal 
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        cartItems={cartItems}
        getCartTotal={getTotalPrice}
        clearCart={clearCart}
      />
    </>
  );
};

export default Cart;